/*
 *******************************************************************************
 *
 *  Filename:   @/redux/reducerHelpers.js
 *
 *  Synopsis:   Common reducer helper functions for Redux slices.
 *
 *  Author:     Norman J. Nolasco [Archetact Software]
 *
 *  Created:    Saturday, January 13, 2024 - 9:36 PM (CST)
 *
 *  Notes:      Contains reusable reducer functions for field updates,
 *              including type-checked and integer-coerced updates.
 *
 *  Revisions:
 *              2024/01/13  NJN Initial Build
 *              2025/06/01  NJN Modularized helpers for reuse across slices
 *
 *  Copyright (c) 2024-2025 Archetact Software - All Rights Reserved.
 *  Unauthorized copying of this file via any medium is strictly prohibited.
 *  Proprietary and Confidential.
 *
 *******************************************************************************
 */

/**
 * Standardized field update reducer.
 * @param {object} state - Redux state slice.
 * @param {object} action - Redux action.
 * @param {string} field - Field to update.
 * @param {Array<string>} intFields - Array of fields that should be parsed as integers.
 */
export const updateFieldReducer = (state, action, field, intFields = []) => {
  const value = action.payload ? action.payload[field] : undefined;

  if (value !== undefined) {
    state[field] = intFields.includes(field) ? parseInt(value, 10) : value;
    state.error = Number.isNaN(state[field]) ? `Invalid value for ${field}.` : null;
  } else {
    state.error = `No ${field} provided.`;
  }
};

/**
 * Type-checked field update reducer.
 * @param {object} state - Redux state slice.
 * @param {object} action - Redux action.
 * @param {string} field - Field to update.
 * @param {object} initialState - The initial state (for type comparison).
 */
export const updateTypedFieldReducer = (state, action, field, initialState) => {
  const expectedType = Array.isArray(initialState[field]) ? "array" : typeof initialState[field];
  const value = action.payload;

  let valueType = typeof value;
  if (Array.isArray(value)) valueType = "array";

  if (valueType === expectedType) {
    state[field] = value;
    state.error = null;
  } else {
    state.error = `Type mismatch for ${field}: expected ${expectedType}, got ${valueType}`;
  }
};
